/**
 * Clerk satellite configuration for the browser SPAs (per ADR-0091).
 *
 * Regta's tenant-instance SPAs — the per-owner portals at `<slug>.regta.se`
 * and the landing dispatcher — are **satellites** of the group's primary
 * Clerk domain. Two rules from Clerk shape everything here:
 *
 *   1. A satellite must use `proxyUrl` **or** `domain`, never both. We proxy
 *      (`auth.regta.se/__clerk/`), so `domain` is deliberately absent.
 *   2. Sign-in and sign-up must complete on the primary domain. A satellite
 *      may not render `<SignIn/>` / `<SignUp/>`; it links out instead, via
 *      `Clerk.buildSignInUrl()` so the return trip carries the sync marker.
 *
 * All three values arrive together or not at all: an environment is either a
 * satellite or it isn't. Dev is not (Clerk does not proxy development
 * instances), so dev SPAs get `{}` and behave exactly as they always have.
 *
 * Values are passed in rather than read here, because they reach the browser
 * through Vite's `import.meta.env`, which this package cannot see.
 */

export type ClerkSatelliteEnv = {
  /** `VITE_CLERK_PROXY_URL` — e.g. `https://auth.regta.se/__clerk/`. */
  proxyUrl?: string | undefined;
  /** `VITE_CLERK_SIGN_IN_URL` — the primary's sign-in page. */
  signInUrl?: string | undefined;
  /** `VITE_CLERK_SIGN_UP_URL` — the primary's sign-up page. */
  signUpUrl?: string | undefined;
};

export type ClerkSatelliteProps =
  | Record<string, never>
  | {
      isSatellite: true;
      proxyUrl: string;
      signInUrl: string;
      signUpUrl: string;
    };

/**
 * Props to spread onto `<ClerkProvider>`. Empty when this build is not a
 * satellite.
 *
 * A partial configuration returns empty rather than half-arming the SPA: a
 * satellite that knows its proxy but not where to send a user to sign in is
 * a dead end, and failing back to the non-satellite path at least keeps the
 * app usable while the misconfiguration is found.
 */
export function clerkSatelliteProps(env: ClerkSatelliteEnv): ClerkSatelliteProps {
  const proxyUrl = env.proxyUrl?.trim();
  const signInUrl = env.signInUrl?.trim();
  const signUpUrl = env.signUpUrl?.trim();
  if (!proxyUrl || !signInUrl || !signUpUrl) return {};
  return { isSatellite: true, proxyUrl, signInUrl, signUpUrl };
}

/** True when this build must link out to the primary instead of rendering `<SignIn/>`. */
export function isClerkSatellite(props: ClerkSatelliteProps): boolean {
  return 'isSatellite' in props;
}

/**
 * Where a satellite must send the user back through, instead of straight to
 * `target`, when linking out to the primary for sign-in.
 *
 * clerk-js is supposed to fire its own session sync (reading the primary's
 * `__client_uat` cookies) on the way back from the primary and needs no help
 * — but on this topology that sync has not been firing reliably, and a user
 * landing directly on `target` gets treated as still signed out. Routing the
 * return trip through the proxy's own `/v1/client/sync` first forces the
 * sync explicitly; `target` survives as its `redirect_url` query param for
 * the hop after that.
 *
 * Built off `proxyUrl` rather than a hardcoded host so it can never drift
 * from the `ClerkProvider` config — same value, one source.
 */
export function buildSyncRedirectUrl(proxyUrl: string, target: string): string {
  return `${proxyUrl}/v1/client/sync?redirect_url=${encodeURIComponent(target)}`;
}
